import useApi from 'common/hooks/useApi';
import useTypedSelector from 'common/hooks/useTypedSelector';
import ActionStatus from 'common/types/ActionStatus';
import ResidenceUpload from 'domain/components/application/upload/ResidenceUpload';
import upload, {
  FileToUpload,
  getAttachment,
} from 'domain/components/application/upload/upload';
import FormVerificationResponse from 'domain/components/verify-identity/FormVerificationResponse';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { GroupDetailsContext } from 'features/group-details/GroupDetailsContainer';
import { GroupReferenceKey } from 'features/cart/CartSlice';
import ChsAttachment from 'domain/types/ChsAttachment';

interface IProofContainer {
  groupRefKey: GroupReferenceKey;
  onSuccess: () => void;
}

/**
 * We need to use Group Reference Key.
 * @param props
 * @returns
 */
const ProofContainer = (props: IProofContainer) => {
  // Handles new selected files by the user.
  const [file1, setFile1] = React.useState<File | null>(null);
  const [file2, setFile2] = React.useState<File | null>(null);
  // Handles stored attached files by the user.
  const [validatedFile1, setValidatedFile1] =
    React.useState<ChsAttachment | null>(null);
  const [validatedFile2, setValidatedFile2] =
    React.useState<ChsAttachment | null>(null);

  const [attachmentType1Cd, setAttachmentType1Cd] = React.useState<
    string | null
  >(null);
  const [attachmentType2Cd, setAttachmentType2Cd] = React.useState<
    string | null
  >(null);
  const { access_token } = useTypedSelector((state) => state.auth);
  const [actionStatus, setActionStatus] = useState(ActionStatus.none);
  const [msg, setMessage] = useState('');
  const groupDetailsContext = useContext(GroupDetailsContext);

  const [{ data: verification }, fetchStatus] =
    useApi<FormVerificationResponse>({
      url: '',
      method: 'GET',
    });

  const { groupRefKey } = props;

  const refreshVerification = useCallback(() => {
    if (groupRefKey !== null) {
      fetchStatus(
        undefined,
        `/applications/primary-residence-status?groupRefKey=` + groupRefKey
      );
    }
  }, [fetchStatus, groupRefKey]);

  // Get primary residence status on page load
  useEffect(() => {
    refreshVerification();
  }, [refreshVerification]);

  useEffect(() => {
    if (verification && verification.primaryResAttachment) {
      const attachedFile1 = getAttachment(verification, 'PRIMARY_RESIDENCE');
      const attachedFile2 = getAttachment(verification, 'PRIMARY_RESIDENCE_02');
      if (attachedFile1) {
        setValidatedFile1(attachedFile1);
        setAttachmentType1Cd(attachedFile1.attachmentTypeCd);
      }

      if (attachedFile2) {
        setValidatedFile2(attachedFile2);
        setAttachmentType2Cd(attachedFile2.attachmentTypeCd);
      }
    }
  }, [verification]);

  const handleSubmit = () => {
    if (
      !access_token ||
      (!file1 && !validatedFile1) ||
      (!file2 && !validatedFile2) ||
      !attachmentType1Cd ||
      !attachmentType2Cd
    ) {
      console.error('Cannot submit files. Missing information.');
      return;
    }

    // Uploaded files, but no changes to submit
    if (!file1 && !file2 && validatedFile1 && validatedFile2) {
      props.onSuccess();
    }

    const files: FileToUpload[] = [];
    if (file1) {
      files.push({
        files: file1,
        attachmentTypeCd: attachmentType1Cd,
        uploadTypeCd: 'PRIMARY_RESIDENCE',
      });
    }

    if (file2) {
      files.push({
        files: file2,
        attachmentTypeCd: attachmentType2Cd,
        uploadTypeCd: 'PRIMARY_RESIDENCE_02',
      });
    }

    setActionStatus(ActionStatus.saving);
    upload(
      groupRefKey,
      access_token,
      '/attachments/primary-residence',
      true,
      null,
      null,
      undefined,
      undefined,
      files
    )
      .then(() => {
        groupDetailsContext.onRefresh();
        setMessage('Successfully updated primary residence proof.');
        setActionStatus(ActionStatus.saved);
        props.onSuccess();
      })
      .catch((e) => {
        setMessage(e);
        setActionStatus(ActionStatus.failed);
      });
  };

  return (
    <>
      <ResidenceUpload
        showValidations={actionStatus === ActionStatus.none}
        verification={verification}
        uploadStatus={actionStatus}
        uploadMessage={msg}
        onDocument1Change={(e) => {
          const { files } = e.target;
          if (files) {
            setFile1(files[0]);
          } else {
            setFile1(null);
          }
        }}
        onDocument2Change={(e) => {
          const { files } = e.target;
          if (files) {
            setFile2(files[0]);
          } else {
            setFile2(null);
          }
        }}
        onAttachmentType1Change={useCallback(
          (value: string) => setAttachmentType1Cd(value),
          []
        )}
        onAttachmentType2Change={useCallback(
          (value: string) => setAttachmentType2Cd(value),
          []
        )}
        error={undefined}
        filesLoaded={
          (Boolean(file1) && Boolean(file2)) ||
          (Boolean(validatedFile1) && Boolean(validatedFile2))
        }
        colSize={12}
        attachment1={file1}
        attachment2={file2}
        hideExistingDisplay
      />
      {}
      <Button type='button' onClick={handleSubmit}>
        Submit
      </Button>
    </>
  );
};

export default ProofContainer;
